import '@mui/x-data-grid-premium/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { omneaDataGridClasses } from '../../dataGridClasses'
import { menuContainerStyle, menuListStyle } from './MuiMenu'

export const MuiDataGrid: Components<Omit<Theme, 'components'>>['MuiDataGrid'] = {
  defaultProps: {
    slotProps: {
      // e.g. icon buttons in the headers for sort/filter
      baseIconButton: {
        variant: 'ghost',
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      '--DataGrid-overlayHeight': '300px',
      '--unstable_DataGrid-radius': theme.shape['radius-2xl'],
      '--DataGrid-rowBorderColor': theme.palette.border.strong,
      '--DataGrid-pinnedBackground': theme.palette.bg.surface.default,
      '--DataGrid-containerBackground': theme.palette.bg.surface.default,
      borderWidth: theme.strokeWidth.default,
      borderColor: theme.palette.border.strong,
      color: theme.palette.text.default,

      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,

      [`&.${omneaDataGridClasses.roundTop}`]: {
        borderTopLeftRadius: theme.shape['radius-2xl'],
        borderTopRightRadius: theme.shape['radius-2xl'],
      },
      // We no longer actually round the first child, we just round the whole thing
      [`&.${omneaDataGridClasses.roundFirstChildTop}`]: {
        borderTopLeftRadius: theme.shape['radius-2xl'],
        borderTopRightRadius: theme.shape['radius-2xl'],
      },

      '& .MuiDataGrid-withBorderColor': {
        borderColor: theme.palette.border.strong,
      },

      [`&.${omneaDataGridClasses.allowPageScroll}`]: {
        '& .MuiDataGrid-virtualScroller': {
          overflowY: 'visible !important',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },
      // Direct filler overrides don't work?
      '& .MuiDataGrid-filler > div': {
        borderTopWidth: theme.strokeWidth.default,
      },
    }),
    cell: ({ theme }) => ({
      padding: theme.spacing(3),
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
      borderTopWidth: theme.strokeWidth.default,
      alignItems: 'center',
      display: 'flex',
    }),
    columnHeader: ({ theme }) => ({
      padding: theme.spacing(3),
      ...theme.typography['body-medium'],
      color: theme.palette.text.muted,
    }),
    'row--borderBottom': ({ theme }) => ({
      '& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
        borderBottomWidth: theme.strokeWidth.default,
      },
    }),
    'container--top': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.bg.surface.subtle,
      },
    }),
    'container--bottom': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.bg.surface.subtle,
      },
    }),
    row: ({ theme }) => ({
      // DataGridBase uses a nested selector and !important, so we have to do a lot to override it here
      // We should remove most of that styling and move it here
      '&[role="row"]': {
        borderWidth: `${theme.strokeWidth.default} !important`,
        borderColor: theme.palette.border.strong,
      },
      '&:hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
    }),
    columnSeparator: {
      opacity: 0,
    },
    virtualScroller: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    menu: ({ theme }) => ({
      '& .MuiPaper-root': menuContainerStyle(theme),
      '& .MuiDataGrid-menuList': menuListStyle(theme),
    }),
    panel: ({ theme }) => ({
      boxShadow: theme.customShadows.sm,
    }),
    footerContainer: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
      borderTopWidth: theme.strokeWidth.default,
    }),
    pinnedColumns: {
      zIndex: 1,
    },
    'columnHeader--pinnedLeft': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'columnHeader--pinnedRight': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'cell--pinnedLeft': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'cell--pinnedRight': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'filler--pinnedLeft': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'filler--pinnedRight': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    paper: ({ theme }) => ({
      borderWidth: theme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.border.subtle,
      borderRadius: theme.shape['radius-sm'],
      color: theme.palette.text.default,
      backgroundColor: theme.palette.bg.surface.elevated,
    }),
    columnHeaderTitleContainer: {
      // Sort icon in header shouldn't be clipped
      overflow: 'unset',
    },
    columnHeaderTitleContainerContent: {
      // Move sort icon to the right
      width: '100%',
    },
    aggregationColumnHeaderLabel: ({ theme }) => ({
      ...theme.typography['body-regular'],
    }),
    filterIcon: ({ theme }) => ({
      color: theme.palette.fg.muted,
    }),
    filterForm: ({ theme }) => ({
      gap: theme.spacing(1),
    }),
    panelFooter: ({ theme }) => ({
      marginTop: theme.spacing(4),
    }),
    columnsManagement: ({ theme }) => ({
      padding: theme.spacing(2),
      '& .MuiDataGrid-columnsManagementRow': {
        // Default is a negative margin
        marginLeft: 0,
        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
    columnsManagementFooter: ({ theme }) => ({
      borderTopWidth: theme.strokeWidth.default,
      borderColor: theme.palette.border.subtle,
      padding: theme.spacing(2),
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,

        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
  },
}
